import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuBot,
  LuChevronLeft,
  LuChevronRight,
  LuDownload,
  LuEye,
  LuFileText,
  LuPaperclip,
  LuRefreshCw,
  LuSearch,
  LuUserCheck,
  LuUserCog,
  LuUserPlus,
  LuX,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
  assignManagerTicket,
  fetchManagerTicketAssignees,
  fetchManagerTicketById,
  fetchManagerTickets,
  fetchSuperAdminTicketById,
  fetchSuperAdminTickets,
  ticketExtractMessage,
} from "../../services/supportTicket.service";
import "../../assets/styles/super_admin_access.scss";

const PAGE_OPTIONS = [10, 20, 50];

const ASSIGNMENT_OPTIONS = [
  { value: "", label: "All Assignment Types" },
  { value: "unassigned", label: "Unassigned" },
  { value: "manual", label: "Manually Assigned" },
  { value: "automatic", label: "Automatically Assigned" },
];

const TAB_OPTIONS = [
  { id: "all", label: "All Tickets" },
  { id: "unassigned", label: "Unassigned" },
  { id: "assigned-to-me", label: "My Assigned Tickets", managerOnly: true },
];

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date
    .toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .replace(",", "");
}

function formatStatus(status) {
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function truncateText(value, max = 28) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function nameInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "SU";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function assignmentBadge(type) {
  if (type === "manual") {
    return { label: "Manual", className: "sa-dispute-badge sa-dispute-badge--manual" };
  }
  if (type === "automatic") {
    return { label: "Auto", className: "sa-dispute-badge sa-dispute-badge--auto" };
  }
  return { label: "Unassigned", className: "sa-dispute-badge sa-dispute-badge--unassigned" };
}

function priorityBadge(priority) {
  const normalized = String(priority || "").toLowerCase();
  if (normalized === "urgent") return "sa-dispute-priority sa-dispute-priority--urgent";
  if (normalized === "high") return "sa-dispute-priority sa-dispute-priority--high";
  if (normalized === "medium") return "sa-dispute-priority sa-dispute-priority--medium";
  return "sa-dispute-priority sa-dispute-priority--low";
}

function statusBadge(status) {
  const normalized = String(status || "").toLowerCase();
  return `sa-dispute-status sa-dispute-status--${normalized.replace(/\s+/g, "_")}`;
}

function escapeCsv(value) {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 7) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export default function SuperAdminTicketManagementPage({
  basePath = "/super-admin",
  canAssign = false,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [priority, setPriority] = useState(() => searchParams.get("priority") || "");
  const [assignmentType, setAssignmentType] = useState(
    () => searchParams.get("assignmentType") || "",
  );
  const [assignedToMe, setAssignedToMe] = useState(
    () => canAssign && searchParams.get("assignedToMe") === "1",
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [viewTicket, setViewTicket] = useState(null);
  const [viewTicketLoading, setViewTicketLoading] = useState(false);
  const [viewTicketError, setViewTicketError] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assigneeId, setAssigneeId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [globalStats, setGlobalStats] = useState({
    total: 0,
    unassigned: 0,
    manual: 0,
    automatic: 0,
  });

  const listTickets = canAssign ? fetchManagerTickets : fetchSuperAdminTickets;
  const getTicketById = canAssign ? fetchManagerTicketById : fetchSuperAdminTicketById;
  const ticketsBasePath = `${basePath}/tickets`;
  const dashboardPath = canAssign ? "/home" : "/super-admin/dashboard";

  const activeTab = useMemo(() => {
    if (assignedToMe) return "assigned-to-me";
    if (assignmentType === "unassigned") return "unassigned";
    return "all";
  }, [assignmentType, assignedToMe]);

  const visibleTabs = useMemo(
    () => TAB_OPTIONS.filter((tab) => !tab.managerOnly || canAssign),
    [canAssign],
  );

  const syncUrlFilters = (next) => {
    const params = new URLSearchParams();
    if (next.status) params.set("status", next.status);
    if (next.priority) params.set("priority", next.priority);
    if (next.assignmentType) params.set("assignmentType", next.assignmentType);
    if (next.assignedToMe) params.set("assignedToMe", "1");
    setSearchParams(params, { replace: true });
  };

  const resetFilters = () => {
    setQuery("");
    setStatus("");
    setPriority("");
    setAssignmentType("");
    setAssignedToMe(false);
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const handleTabChange = (tabId) => {
    setPage(1);
    if (tabId === "all") {
      setAssignmentType("");
      setAssignedToMe(false);
      syncUrlFilters({ status, priority });
      return;
    }
    if (tabId === "unassigned") {
      setAssignmentType("unassigned");
      setAssignedToMe(false);
      syncUrlFilters({ assignmentType: "unassigned", status, priority });
      return;
    }
    if (tabId === "assigned-to-me") {
      if (!canAssign) return;
      setAssignmentType("");
      setAssignedToMe(true);
      syncUrlFilters({ assignedToMe: true, status, priority });
    }
  };

  useEffect(() => {
    setStatus(searchParams.get("status") || "");
    setPriority(searchParams.get("priority") || "");
    setAssignmentType(searchParams.get("assignmentType") || "");
    setAssignedToMe(canAssign && searchParams.get("assignedToMe") === "1");
  }, [canAssign, searchParams]);

  const loadGlobalStats = useCallback(async () => {
    try {
      const [allRes, unassignedRes, manualRes, autoRes] = await Promise.all([
        listTickets({ page: 1, pageSize: 1 }),
        listTickets({ assignmentType: "unassigned", page: 1, pageSize: 1 }),
        listTickets({ assignmentType: "manual", page: 1, pageSize: 1 }),
        listTickets({ assignmentType: "automatic", page: 1, pageSize: 1 }),
      ]);
      setGlobalStats({
        total: Math.trunc(Number(allRes.total) || 0),
        unassigned: Math.trunc(Number(unassignedRes.total) || 0),
        manual: Math.trunc(Number(manualRes.total) || 0),
        automatic: Math.trunc(Number(autoRes.total) || 0),
      });
    } catch {
      // Keep prior stats on failure.
    }
  }, [listTickets]);

  useEffect(() => {
    loadGlobalStats();
  }, [loadGlobalStats]);

  useEffect(() => {
    if (!canAssign) return undefined;
    let cancelled = false;
    fetchManagerTicketAssignees()
      .then((data) => {
        if (!cancelled) setAssignees(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setAssignees([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canAssign]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTickets({
        search: query.trim() || undefined,
        status: status || undefined,
        priority: priority || undefined,
        assignmentType: assignedToMe ? undefined : assignmentType || undefined,
        assignedToMe: assignedToMe ? "1" : undefined,
        page,
        pageSize,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Math.trunc(Number(data.total) || 0));
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to load tickets."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [assignedToMe, assignmentType, listTickets, page, pageSize, priority, query, status]);

  useEffect(() => {
    const timer = setTimeout(loadTickets, 250);
    return () => clearTimeout(timer);
  }, [loadTickets]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const refreshAll = async () => {
    await Promise.all([loadTickets(), loadGlobalStats()]);
  };

  const handleView = (ticket) => {
    navigate(`${ticketsBasePath}/${ticket.id}`);
  };

  const closeQuickView = () => {
    setViewTicket(null);
    setViewTicketLoading(false);
    setViewTicketError("");
  };

  const openQuickView = async (ticket) => {
    const ticketId = Number(ticket?.id);
    if (!Number.isFinite(ticketId) || ticketId <= 0) return;
    setViewTicket({ ticket, attachments: [], activities: [], messages: [] });
    setViewTicketError("");
    setViewTicketLoading(true);
    try {
      const detail = await getTicketById(ticketId);
      if (detail?.ticket) {
        setViewTicket(detail);
      } else {
        setViewTicketError("Ticket details are unavailable.");
      }
    } catch (error) {
      setViewTicketError(ticketExtractMessage(error, "Failed to load ticket details."));
    } finally {
      setViewTicketLoading(false);
    }
  };

  const openAssignModal = (ticket) => {
    setAssignTarget(ticket);
    setAssigneeId(ticket.assignedToAccessUserId || "");
  };

  const submitAssign = async () => {
    if (!assignTarget || !assigneeId) {
      toast.error("Select a support user.");
      return;
    }
    setAssigning(true);
    setBusyId(assignTarget.id);
    try {
      await assignManagerTicket(assignTarget.id, { accessUserId: Number(assigneeId) });
      toast.success(`Ticket ${assignTarget.ticketNumber} assigned successfully.`);
      setAssignTarget(null);
      setAssigneeId("");
      await Promise.all([loadTickets(), loadGlobalStats()]);
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to assign ticket."));
    } finally {
      setAssigning(false);
      setBusyId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await listTickets({
        search: query.trim() || undefined,
        status: status || undefined,
        priority: priority || undefined,
        assignmentType: assignedToMe ? undefined : assignmentType || undefined,
        assignedToMe: assignedToMe ? "1" : undefined,
        page: 1,
        pageSize: 1000,
      });
      const rows = Array.isArray(data.items) ? data.items : [];
      const headers = [
        "Ticket Number",
        "Internal ID",
        "Subject",
        "Customer",
        "Customer Email",
        "Category",
        "Priority",
        "Status",
        "Assignment",
        "Assignee",
        "Assignee Email",
        "SLA",
        "Created",
      ];
      const csvLines = [
        headers.join(","),
        ...rows.map((ticket) =>
          [
            ticket.ticketNumber,
            ticket.id,
            ticket.subject || "",
            ticket.customerName || "",
            ticket.customerEmail || "",
            ticket.categoryLabel || "",
            ticket.priority || "",
            ticket.statusLabel || ticket.status || "",
            ticket.assignmentType || "",
            ticket.assignedToName || "",
            ticket.assignedToEmail || "",
            ticket.slaRemainingLabel || "",
            ticket.createdAt || "",
          ]
            .map(escapeCsv)
            .join(","),
        ),
      ];
      const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "support-tickets.csv";
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} ticket${rows.length === 1 ? "" : "s"}.`);
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to export tickets."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="sa-access-page sa-dispute-page">
      <header className="sa-access-head">
        <div>
          <nav className="sa-access-breadcrumb" aria-label="Breadcrumb">
            <Link to={dashboardPath}>Dashboard</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">Ticket Management</span>
          </nav>
          <h1>Ticket Management</h1>
          <p>
            {canAssign
              ? "Manage team assignments, workload, SLA, and ticket history."
              : "Read-only oversight of support tickets, conversations, assignments, and audit history."}
          </p>
        </div>
        <div className="sa-access-actions">
          <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={refreshAll}>
            <LuRefreshCw aria-hidden /> Refresh
          </button>
        </div>
      </header>

      <section className="sa-access-stats">
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--blue">
            <LuFileText />
          </span>
          <div>
            <strong>{globalStats.total.toLocaleString()}</strong>
            <span>Total Tickets</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--orange">
            <LuUserPlus />
          </span>
          <div>
            <strong>{globalStats.unassigned.toLocaleString()}</strong>
            <span>Unassigned</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--green">
            <LuUserCheck />
          </span>
          <div>
            <strong>{globalStats.manual.toLocaleString()}</strong>
            <span>Manually Assigned</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--purple">
            <LuBot />
          </span>
          <div>
            <strong>{globalStats.automatic.toLocaleString()}</strong>
            <span>Auto Assigned</span>
          </div>
        </article>
      </section>

      <div
        className="sa-access-toolbar sa-dispute-toolbar"
        style={{
          gridTemplateColumns: "minmax(220px, 1.6fr) repeat(3, minmax(140px, 1fr)) auto",
        }}
      >
        <label className="sa-access-search">
          <LuSearch aria-hidden />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search by Ticket ID, Customer, Subject..."
          />
        </label>
        <select
          value={status}
          onChange={(event) => {
            const next = event.target.value;
            setStatus(next);
            setPage(1);
            syncUrlFilters({
              status: next,
              priority,
              assignmentType,
              assignedToMe,
            });
          }}
        >
          {TICKET_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || "all-status"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(event) => {
            const next = event.target.value;
            setPriority(next);
            setPage(1);
            syncUrlFilters({
              status,
              priority: next,
              assignmentType,
              assignedToMe,
            });
          }}
        >
          {TICKET_PRIORITY_OPTIONS.map((opt) => (
            <option key={opt.value || "all-priority"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={assignmentType}
          onChange={(event) => {
            const next = event.target.value;
            setAssignmentType(next);
            setAssignedToMe(next ? false : assignedToMe);
            setPage(1);
            syncUrlFilters({
              status,
              priority,
              assignmentType: next,
              assignedToMe: next ? false : assignedToMe,
            });
          }}
        >
          {ASSIGNMENT_OPTIONS.map((opt) => (
            <option key={opt.value || "all-assignment"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={resetFilters}>
          Reset
        </button>
      </div>

      <section className="sa-access-card sa-dispute-card">
        <div className="sa-dispute-card-toolbar">
          <div className="sa-dispute-tabs" role="tablist" aria-label="Ticket views">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`sa-dispute-tabs__btn${activeTab === tab.id ? " sa-dispute-tabs__btn--active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="sa-dispute-card-toolbar__actions">
            <button
              type="button"
              className="sa-access-btn sa-access-btn--ghost"
              disabled={exporting}
              onClick={handleExport}
            >
              <LuDownload aria-hidden /> Export
            </button>
          </div>
        </div>

        <div className="sa-access-table-wrap">
          <table className="sa-access-table sa-dispute-table">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Subject</th>
                <th>Customer</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Assignment</th>
                <th>Assignee</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="sa-access-table__empty">
                    Loading tickets...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="sa-access-table__empty">
                    No tickets found.
                  </td>
                </tr>
              ) : (
                items.map((ticket) => {
                  const assignBadge = assignmentBadge(ticket.assignmentType);
                  const isUnassigned = ticket.assignmentType === "unassigned";
                  const canReassign =
                    !isUnassigned && String(ticket.status || "").toLowerCase() === "assigned";
                  return (
                    <tr key={ticket.id}>
                      <td>
                        <div className="sa-dispute-id-cell">
                          <button
                            type="button"
                            className="sa-dispute-id-link"
                            title={ticket.ticketNumber}
                            onClick={() => openQuickView(ticket)}
                          >
                            {ticket.ticketNumber}
                          </button>
                          <span className="sa-dispute-id-chip">
                            #{String(ticket.id).padStart(6, "0")}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="sa-dispute-stack">
                          <span className="sa-dispute-stack__title" title={ticket.subject || undefined}>
                            {truncateText(ticket.subject || "—", 28)}
                          </span>
                          {ticket.referenceNumber ? (
                            <span className="sa-dispute-stack__meta" title={ticket.referenceNumber}>
                              Ref: {truncateText(ticket.referenceNumber, 22)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="sa-dispute-stack">
                          <span className="sa-dispute-stack__title" title={ticket.customerName}>
                            {truncateText(ticket.customerName, 20)}
                          </span>
                          <span
                            className="sa-dispute-stack__meta"
                            title={ticket.customerEmail || undefined}
                          >
                            {truncateText(ticket.customerEmail || "—", 24)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="sa-dispute-stack__meta">
                          {truncateText(ticket.categoryLabel || "—", 22)}
                        </span>
                      </td>
                      <td>
                        <span className={priorityBadge(ticket.priority)}>{ticket.priority}</span>
                      </td>
                      <td>
                        <span className={statusBadge(ticket.status)}>
                          {formatStatus(ticket.status)}
                        </span>
                      </td>
                      <td>
                        <span className="sa-dispute-date">{formatDateTime(ticket.createdAt)}</span>
                      </td>
                      <td>
                        <span className={assignBadge.className}>{assignBadge.label}</span>
                      </td>
                      <td>
                        {ticket.assignedToName ? (
                          <div className="sa-dispute-support-user">
                            <span className="sa-dispute-avatar">
                              {nameInitials(ticket.assignedToName)}
                            </span>
                            <div className="sa-dispute-stack">
                              <span
                                className="sa-dispute-stack__title"
                                title={ticket.assignedToName}
                              >
                                {truncateText(ticket.assignedToName, 18)}
                              </span>
                              <span
                                className="sa-dispute-stack__meta"
                                title={ticket.assignedToEmail || undefined}
                              >
                                {truncateText(ticket.assignedToEmail || "Support", 22)}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="sa-dispute-stack__meta">—</span>
                        )}
                      </td>
                      <td>
                        <div className="sa-dispute-actions">
                          <button
                            type="button"
                            className="sa-dispute-icon-btn"
                            title="View details"
                            disabled={busyId === ticket.id}
                            onClick={() => handleView(ticket)}
                          >
                            <LuEye aria-hidden />
                          </button>
                          {canAssign && (isUnassigned || canReassign) ? (
                            <button
                              type="button"
                              className={`sa-dispute-icon-btn ${
                                isUnassigned
                                  ? "sa-dispute-icon-btn--assign"
                                  : "sa-dispute-icon-btn--reassign"
                              }`}
                              title={isUnassigned ? "Assign ticket" : "Reassign ticket"}
                              disabled={busyId === ticket.id}
                              onClick={() => openAssignModal(ticket)}
                            >
                              {isUnassigned ? <LuUserPlus aria-hidden /> : <LuUserCog aria-hidden />}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="sa-access-footer">
          <span>
            Showing {items.length ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, total)} of {total} tickets
          </span>
          <div className="sa-access-pagination">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              <LuChevronLeft aria-hidden />
            </button>
            {pageNumbers.map((item) => (
              <button
                key={item}
                type="button"
                className={currentPage === item ? "is-active" : ""}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            >
              <LuChevronRight aria-hidden />
            </button>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </footer>
      </section>

      {viewTicket?.ticket
        ? createPortal(
            <div
              className="sa-access-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sa-ticket-modal-title"
              onClick={closeQuickView}
            >
              <div
                className="sa-access-modal__panel sa-dispute-modal__panel"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="sa-dispute-modal__head">
                  <div className="sa-dispute-modal__head-main">
                    <div className="sa-dispute-modal__title-row">
                      <h2 id="sa-ticket-modal-title">{viewTicket.ticket.ticketNumber}</h2>
                      <span className="sa-dispute-id-chip">
                        #{String(viewTicket.ticket.id).padStart(6, "0")}
                      </span>
                    </div>
                    <p className="sa-dispute-modal__subject">{viewTicket.ticket.subject}</p>
                    <div className="sa-dispute-modal__badges">
                      <span className={priorityBadge(viewTicket.ticket.priority)}>
                        {viewTicket.ticket.priority}
                      </span>
                      <span className={statusBadge(viewTicket.ticket.status)}>
                        {formatStatus(viewTicket.ticket.status)}
                      </span>
                      <span className={assignmentBadge(viewTicket.ticket.assignmentType).className}>
                        {assignmentBadge(viewTicket.ticket.assignmentType).label}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sa-dispute-icon-btn"
                    aria-label="Close"
                    onClick={closeQuickView}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="sa-dispute-modal__body">
                  {viewTicketLoading ? (
                    <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                      Loading ticket details...
                    </p>
                  ) : null}
                  {viewTicketError ? (
                    <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                      {viewTicketError}
                    </p>
                  ) : null}

                  <section className="sa-dispute-modal__section">
                    <h3>Ticket Details</h3>
                    <div className="sa-dispute-modal__fields">
                      <div className="sa-dispute-modal__field">
                        <span>Category</span>
                        <strong>{viewTicket.ticket.categoryLabel || "—"}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Affected Module</span>
                        <strong>{viewTicket.ticket.affectedModule || "—"}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Reference</span>
                        <strong>{viewTicket.ticket.referenceNumber || "—"}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Submitted</span>
                        <strong>{formatDateTime(viewTicket.ticket.createdAt)}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>SLA</span>
                        <strong>{viewTicket.ticket.slaRemainingLabel || "—"}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Expected Response</span>
                        <strong>{viewTicket.ticket.expectedResponseLabel || "—"}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Description</h3>
                    <p className="sa-dispute-modal__description">
                      {viewTicket.ticket.description || "No description provided."}
                    </p>
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Customer</h3>
                    <div className="sa-dispute-modal__parties">
                      <article className="sa-dispute-party-card">
                        <span className="sa-dispute-party-card__label">Requester</span>
                        <div className="sa-dispute-support-user">
                          <span className="sa-dispute-avatar">
                            {nameInitials(viewTicket.ticket.customerName)}
                          </span>
                          <div className="sa-dispute-stack">
                            <span className="sa-dispute-stack__title">
                              {viewTicket.ticket.customerName || "—"}
                            </span>
                            <span className="sa-dispute-stack__meta">
                              {viewTicket.ticket.customerEmail || "—"}
                            </span>
                            {viewTicket.ticket.customerPhone ? (
                              <span className="sa-dispute-stack__meta">
                                {viewTicket.ticket.customerCountryCode
                                  ? `${viewTicket.ticket.customerCountryCode} `
                                  : ""}
                                {viewTicket.ticket.customerPhone}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    </div>
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Attachments</h3>
                    {Array.isArray(viewTicket.attachments) && viewTicket.attachments.length > 0 ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        {viewTicket.attachments.map((file) => (
                          <a
                            key={file.id}
                            className="sa-dispute-attachment"
                            href={resolvePublicFileUrl(file.filePath)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span className="sa-dispute-attachment__icon" aria-hidden>
                              <LuPaperclip />
                            </span>
                            <span className="sa-dispute-attachment__meta">
                              <strong>{file.fileName || "View attachment"}</strong>
                              <small>Open / download file</small>
                            </span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                        No attachments submitted with this ticket.
                      </p>
                    )}
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Assignment</h3>
                    <div className="sa-dispute-modal__fields">
                      <div className="sa-dispute-modal__field">
                        <span>Assignee</span>
                        <strong>
                          {viewTicket.ticket.assignedToName || "Unassigned"}
                          {viewTicket.ticket.assignedToEmail
                            ? ` · ${viewTicket.ticket.assignedToEmail}`
                            : ""}
                        </strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Manager</span>
                        <strong>{viewTicket.ticket.managerName || "—"}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Assigned At</span>
                        <strong>{formatDateTime(viewTicket.ticket.assignedAt)}</strong>
                      </div>
                    </div>
                  </section>

                  {Array.isArray(viewTicket.activities) && viewTicket.activities.length > 0 ? (
                    <section className="sa-dispute-modal__section">
                      <h3>Activity History</h3>
                      <ul className="sa-dispute-timeline">
                        {viewTicket.activities.map((entry) => (
                          <li key={entry.id}>
                            <span className={statusBadge(entry.action || "new")}>
                              {formatStatus(entry.action || "activity")}
                            </span>
                            <div>
                              <strong>{entry.title || "Activity"}</strong>
                              <small>
                                {formatDateTime(entry.createdAt)}
                                {entry.performedByName ? ` · by ${entry.performedByName}` : ""}
                              </small>
                              {entry.remarks ? (
                                <div className="sa-dispute-stack__meta">{entry.remarks}</div>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                </div>

                <footer className="sa-access-modal__footer sa-dispute-modal__footer">
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--ghost"
                    onClick={closeQuickView}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--ghost"
                    onClick={() => {
                      closeQuickView();
                      handleView(viewTicket.ticket);
                    }}
                  >
                    Open full details
                  </button>
                  {canAssign && viewTicket.ticket.assignmentType === "unassigned" ? (
                    <button
                      type="button"
                      className="sa-access-btn sa-access-btn--primary"
                      onClick={() => {
                        openAssignModal(viewTicket.ticket);
                        closeQuickView();
                      }}
                    >
                      Assign
                    </button>
                  ) : canAssign &&
                    String(viewTicket.ticket.status || "").toLowerCase() === "assigned" ? (
                    <button
                      type="button"
                      className="sa-access-btn sa-access-btn--primary"
                      onClick={() => {
                        openAssignModal(viewTicket.ticket);
                        closeQuickView();
                      }}
                    >
                      Reassign
                    </button>
                  ) : null}
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}

      {assignTarget
        ? createPortal(
            <div
              className="sa-access-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sa-ticket-assign-title"
              onClick={() => setAssignTarget(null)}
            >
              <div
                className="sa-access-modal__panel sa-dispute-assign-modal"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="sa-dispute-assign-modal__head">
                  <div className="sa-dispute-assign-modal__title-wrap">
                    <span
                      className={`sa-dispute-assign-modal__icon ${
                        assignTarget.assignmentType !== "unassigned"
                          ? "sa-dispute-assign-modal__icon--reassign"
                          : "sa-dispute-assign-modal__icon--assign"
                      }`}
                      aria-hidden
                    >
                      {assignTarget.assignmentType !== "unassigned" ? (
                        <LuUserCog />
                      ) : (
                        <LuUserPlus />
                      )}
                    </span>
                    <div className="sa-dispute-modal__head-main">
                      <h2 id="sa-ticket-assign-title">
                        {assignTarget.assignmentType !== "unassigned"
                          ? "Reassign Ticket"
                          : "Assign Ticket"}
                      </h2>
                      <p className="sa-dispute-assign-modal__lead">
                        Choose yourself or an available Support User from your team.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sa-dispute-icon-btn"
                    aria-label="Close"
                    onClick={() => setAssignTarget(null)}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="sa-dispute-assign-modal__body">
                  <div className="sa-dispute-assign-modal__summary">
                    <div className="sa-dispute-assign-modal__summary-top">
                      <strong>{assignTarget.ticketNumber}</strong>
                      <span className={assignmentBadge(assignTarget.assignmentType).className}>
                        {assignmentBadge(assignTarget.assignmentType).label}
                      </span>
                      {assignTarget.priority ? (
                        <span className={priorityBadge(assignTarget.priority)}>
                          {assignTarget.priority}
                        </span>
                      ) : null}
                    </div>
                    <p title={assignTarget.subject || undefined}>
                      {assignTarget.subject || "No subject provided"}
                    </p>
                    {assignTarget.assignmentType !== "unassigned" && assignTarget.assignedToName ? (
                      <small>
                        Currently assigned to <em>{assignTarget.assignedToName}</em>
                        {assignTarget.assignedToEmail
                          ? ` · ${assignTarget.assignedToEmail}`
                          : ""}
                      </small>
                    ) : null}
                  </div>

                  <label className="sa-access-field sa-dispute-assign-modal__field">
                    <span>
                      Assign To <em>*</em>
                    </span>
                    <select
                      value={assigneeId}
                      onChange={(event) => setAssigneeId(event.target.value)}
                    >
                      <option value="">Select an assignee</option>
                      {assignees.some((user) => user.accountType === "support_manager") ? (
                        <optgroup label="Support Managers">
                          {assignees
                            .filter((user) => user.accountType === "support_manager")
                            .map((user) => (
                              <option key={`manager-${user.id}`} value={user.id}>
                                {user.name} ({user.email}) · Me
                              </option>
                            ))}
                        </optgroup>
                      ) : null}
                      <optgroup label="Support Users">
                        {assignees
                          .filter((user) => user.accountType !== "support_manager")
                          .map((user) => (
                            <option key={`support-${user.id}`} value={user.id}>
                              {user.name} ({user.email})
                            </option>
                          ))}
                      </optgroup>
                    </select>
                  </label>

                  <div className="sa-dispute-assign-modal__note" role="note">
                    <LuBot aria-hidden />
                    <p>
                      If still unassigned after the auto-assign delay, the system routes among
                      available support users in your team.
                    </p>
                  </div>
                </div>

                <footer className="sa-dispute-assign-modal__footer">
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--ghost"
                    onClick={() => setAssignTarget(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--primary"
                    disabled={assigning || busyId === assignTarget.id || !assigneeId}
                    onClick={submitAssign}
                  >
                    {assigning ? (
                      "Assigning…"
                    ) : assignTarget.assignmentType !== "unassigned" ? (
                      <>
                        <LuUserCog aria-hidden /> Reassign
                      </>
                    ) : (
                      <>
                        <LuUserPlus aria-hidden /> Assign Ticket
                      </>
                    )}
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
